import { Checkbox, DatePicker, Form, Input, Modal, TimePicker, Typography, Tooltip } from 'antd';
import { CheckOutlined, ClockCircleOutlined, DeleteOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { Button } from '@components/ui/Button';
import { useSchoolTimeConfig } from '@hooks/tenant/useSchoolTimeConfig';
import { useModuleLimit } from '@hooks/tenant/useModuleLimit';
import {
    DashboardContainer,
    SchoolTimeBranchAddressLine,
    SchoolTimeBranchCard,
    SchoolTimeBranchGrid,
    SchoolTimeBranchHeaderLeft,
    SchoolTimeBranchHeaderRow,
    SchoolTimeBranchIconWrap,
    SchoolTimeBranchMeta,
    SchoolTimeBranchSummaryLine,
    SchoolTimeBranchTitleText,
    SchoolTimeCardActionSlot,
    SchoolTimeCardActions,
    SchoolTimeDayLeft,
    SchoolTimeDayRight,
    SchoolTimeDayRow,
    SchoolTimeDaysList,
    SchoolTimeDatePickerWrap,
    SchoolTimeSectionLabel,
    SchoolTimeStack,
    StudentsHeaderActions,
    StudentsHeaderLeft,
    StudentsHeaderRow,
    StudentsHeaderSub,
    StudentsHeaderTitle,
} from './style';

const { Text } = Typography;

export default function SchoolTimeConfig() {
    const {
        scheduleDays,
        branchForm,
        effectiveForm,
        draftBranches,
        dirtyIds,
        saving,
        addOpen,
        effectiveOpen,
        openAdd,
        closeAddModal,
        closeEffectiveModal,
        submitAddBranch,
        removeBranch,
        saveBranch,
        confirmEffectiveSave,
        cancelEdit,
        updateBranchSchedule,
        getBranchSummary,
        toDayjsTime,
        dateFormat,
        dateInputFormats,
    } = useSchoolTimeConfig();

    const pickTemplateTimeForBranch = (branch) => {
        for (const d of scheduleDays) {
            const cur = branch?.schedule?.[d.key];
            if (cur?.on && cur?.from && cur?.to) {
                return { from: cur.from, to: cur.to };
            }
        }
        return { from: null, to: null };
    };

    const { exceeded: limitExceeded } = useModuleLimit({
        moduleKey: 'schoolTime',
        currentCount: draftBranches.length,
    });

    const activeEditingId = dirtyIds.size > 0 ? Array.from(dirtyIds)[0] : null;

    return (
        <DashboardContainer>
            <SchoolTimeStack>
                <StudentsHeaderRow>
                    <StudentsHeaderLeft>
                        <StudentsHeaderTitle>School Time Configuration</StudentsHeaderTitle>
                        <StudentsHeaderSub>
                            Set branch timings and working days. Attendance is locked until this is saved.
                        </StudentsHeaderSub>
                    </StudentsHeaderLeft>
                    <StudentsHeaderActions>
                        <Tooltip title={limitExceeded ? "Limit is exceeded. Please contact EDULINK to change Subscription Plan" : ""}>
                            <Button
                                $primary={false}
                                icon={<PlusOutlined />}
                                onClick={openAdd}
                                disabled={limitExceeded || activeEditingId !== null || saving}
                            >
                                Add Branch
                            </Button>
                        </Tooltip>
                    </StudentsHeaderActions>
                </StudentsHeaderRow>

                <SchoolTimeBranchGrid>
                    {draftBranches.map((b) => {
                        const isEditingThis = b.id === activeEditingId;
                        const hasOtherEditing = activeEditingId !== null && !isEditingThis;
                        return (
                            <SchoolTimeBranchCard key={b.id} styles={{ body: { padding: 14 } }} style={{ opacity: hasOtherEditing ? 0.6 : 1 }}>
                                <SchoolTimeBranchHeaderRow>
                                    <SchoolTimeBranchHeaderLeft>
                                        <SchoolTimeBranchIconWrap aria-hidden>
                                            <ClockCircleOutlined />
                                        </SchoolTimeBranchIconWrap>
                                        <SchoolTimeBranchMeta>
                                            <SchoolTimeBranchTitleText title={b.branchName}>
                                                {b.branchName || '—'}
                                            </SchoolTimeBranchTitleText>
                                            <SchoolTimeBranchSummaryLine>{getBranchSummary(b)}</SchoolTimeBranchSummaryLine>
                                            <SchoolTimeBranchAddressLine title={b.address}>
                                                {b.address || 'No address'}
                                            </SchoolTimeBranchAddressLine>
                                        </SchoolTimeBranchMeta>
                                    </SchoolTimeBranchHeaderLeft>
                                    <SchoolTimeCardActions>
                                        <SchoolTimeCardActionSlot $width={42}>
                                            <Button
                                                type="primary"
                                                loading={saving && dirtyIds.has(b.id)}
                                                disabled={!dirtyIds.has(b.id) || saving || hasOtherEditing}
                                                onClick={() => void saveBranch(b.id)}
                                                aria-label="Save branch"
                                            >
                                                <CheckOutlined />
                                            </Button>
                                        </SchoolTimeCardActionSlot>
                                        {dirtyIds.has(b.id) && (
                                            <SchoolTimeCardActionSlot $width={40}>
                                                <Button
                                                    $primary={false}
                                                    onClick={() => cancelEdit(b.id)}
                                                    aria-label="Cancel edit"
                                                    disabled={saving}
                                                >
                                                    <CloseOutlined />
                                                </Button>
                                            </SchoolTimeCardActionSlot>
                                        )}
                                        <SchoolTimeCardActionSlot $width={40}>
                                            <Button
                                                $primary={false}
                                                danger
                                                aria-label="Remove branch"
                                                onClick={() => void removeBranch(b.id)}
                                                disabled={saving || hasOtherEditing}
                                            >
                                                <DeleteOutlined />
                                            </Button>
                                        </SchoolTimeCardActionSlot>
                                    </SchoolTimeCardActions>
                                </SchoolTimeBranchHeaderRow>

                                <SchoolTimeSectionLabel>Active Days:</SchoolTimeSectionLabel>
                                <SchoolTimeDaysList>
                                    {scheduleDays.map((d) => {
                                        const cur = b?.schedule?.[d.key] || {};
                                        const on = !!cur.on;
                                        return (
                                            <SchoolTimeDayRow key={d.key}>
                                                <SchoolTimeDayLeft>
                                                    <Checkbox
                                                        checked={on}
                                                        disabled={hasOtherEditing}
                                                        onChange={(e) => {
                                                            const nextOn = e.target.checked;
                                                            const template = nextOn ? pickTemplateTimeForBranch(b) : null;
                                                            updateBranchSchedule({
                                                                branchId: b.id,
                                                                dayKey: d.key,
                                                                patch: nextOn
                                                                    ? {
                                                                          on: true,
                                                                          from: cur.from ?? template.from,
                                                                          to: cur.to ?? template.to,
                                                                      }
                                                                    : { on: false, from: null, to: null },
                                                            });
                                                        }}
                                                    />
                                                    <Text strong>{d.label}</Text>
                                                </SchoolTimeDayLeft>
                                                <SchoolTimeDayRight>
                                                    {on ? (
                                                        <>
                                                            <TimePicker
                                                                use12Hours
                                                                format="hh:mm A"
                                                                minuteStep={5}
                                                                placeholder="From"
                                                                value={toDayjsTime(cur.from)}
                                                                disabled={hasOtherEditing}
                                                                onChange={(val) =>
                                                                    updateBranchSchedule({
                                                                        branchId: b.id,
                                                                        dayKey: d.key,
                                                                        patch: { from: val },
                                                                    })
                                                                }
                                                            />
                                                            <Text type="secondary">-</Text>
                                                            <TimePicker
                                                                use12Hours
                                                                format="hh:mm A"
                                                                minuteStep={5}
                                                                placeholder="To"
                                                                value={toDayjsTime(cur.to)}
                                                                disabled={hasOtherEditing}
                                                                onChange={(val) =>
                                                                    updateBranchSchedule({
                                                                        branchId: b.id,
                                                                        dayKey: d.key,
                                                                        patch: { to: val },
                                                                    })
                                                                }
                                                            />
                                                        </>
                                                    ) : (
                                                        <Text type="secondary">OFF</Text>
                                                    )}
                                                </SchoolTimeDayRight>
                                            </SchoolTimeDayRow>
                                        );
                                    })}
                                </SchoolTimeDaysList>
                            </SchoolTimeBranchCard>
                        );
                    })}
                </SchoolTimeBranchGrid>

                {draftBranches.length === 0 ? (
                    <SchoolTimeBranchCard styles={{ body: { padding: 14 } }}>
                        <Text type="secondary">No branches yet. Click “Add Branch” to create one.</Text>
                    </SchoolTimeBranchCard>
                ) : null}

                <Modal
                    title="Add Branch"
                    open={addOpen}
                    onCancel={closeAddModal}
                    onOk={() => void submitAddBranch()}
                    okText="Save branch"
                    confirmLoading={saving}
                    destroyOnClose
                >
                    <Form form={branchForm} layout="vertical">
                        <Form.Item
                            name="branchName"
                            label="Branch name"
                            rules={[{ required: true, message: 'Branch name is required' }]}
                        >
                            <Input placeholder="e.g. Main Campus" />
                        </Form.Item>
                        <Form.Item name="address" label="Address">
                            <Input.TextArea placeholder="Branch address" autoSize={{ minRows: 2, maxRows: 4 }} />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title="Implement date"
                    open={effectiveOpen}
                    onCancel={closeEffectiveModal}
                    onOk={() => void confirmEffectiveSave()}
                    okText="Save"
                    confirmLoading={saving}
                    destroyOnClose
                >
                    <Form form={effectiveForm} layout="vertical">
                        <Form.Item
                            name="effectiveFrom"
                            label="Apply this schedule from"
                            rules={[{ required: true, message: 'Select implement date' }]}
                        >
                            <SchoolTimeDatePickerWrap>
                                <DatePicker placeholder={dateFormat} format={dateInputFormats} />
                            </SchoolTimeDatePickerWrap>
                        </Form.Item>
                        <Text type="secondary">
                            Attendance will show OFF-days (H) only from this date onward.
                        </Text>
                    </Form>
                </Modal>
            </SchoolTimeStack>
        </DashboardContainer>
    );
}
